import React, { useState } from 'react'
import { match as MatchedUrl, useRouteMatch } from 'react-router-dom'
import { Trans, t } from '@lingui/macro'

import { UserModelPublicBaseFilterEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { notification } from 'front-end/src/components/common/notifications/Notifications'

import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { IUser } from './types'
import { navigateTo } from '../../app/util/appHistory'
import { Page } from '../../components/common/page/Page'
import PageHeader from '../../components/common/page/header/PageHeader'
import IconSettings from '../../components/common/icon/IconSettings'
import IconPlus from '../../components/common/icon/IconPlusInCircle'
import { ItemList } from '../../components/common/list/ItemList'
import GroupAvatar from '../../components/common/group/group-avatar/GroupAvatar'
import { GroupDeleteModal } from '../manage-groups-page/components/GroupDeleteModal'
import UserAddToGroupModal from './components/UserAddToGroupModal'
import { GroupListItem } from './components/GroupListItem'
import Filters from './components/Filters'
import IconButton from '../../components/common/form/button/IconButton'
import { UserGroupRoleEnum } from 'types/UserGroup'
import UserRemoveFromGroupModal from './components/UserRemoveFromGroupModal'
import {
  useGroupByIdQuery,
  useUserChangeGroupRoleMutation
} from './GroupPage.codegen'
import { GroupSettingsDrawer } from './components/SettingsDrawer'
import { PageWithSingleAlertAndBackToLogin } from '../PageWithSingleAlert'

import './GroupPage.less'
import { ButtonPrimary, Flex } from 'front-end/src/components'

interface IRouterParams {
  groupId: string
}

interface IProps {
  match: MatchedUrl<IRouterParams>
}

interface IState {
  loading: boolean
  pending: boolean
  page: number
  pageSize: number
  removingUserId: number | null
  search: string
  showDeleteModal: boolean
  showSelectUserModal: boolean
  showSettings: boolean
  removeFromGroupModalSetup: {
    show: boolean
    groupId: number | null
    userId: number | null
  }
}

export const GroupPage: React.FC<IProps> = () => {
  const [state, setState] = useState<IState>({
    loading: false,
    pending: false,
    page: 1,
    pageSize: 20,
    removingUserId: null,
    search: '',
    showDeleteModal: false,
    showSelectUserModal: false,
    showSettings: false,
    removeFromGroupModalSetup: {
      show: false,
      groupId: null,
      userId: null
    }
  })
  const [userChangeGroupRole] = useUserChangeGroupRoleMutation()

  const setStateWithSpread = (partialState) => {
    setState({
      ...state,
      ...partialState
    })
  }
  const {
    showSettings,
    page,
    pageSize,
    search,
    showDeleteModal,
    showSelectUserModal
  } = state
  const { params } = useRouteMatch() as MatchedUrl<{
    groupId: string
  }>

  const variables: {
    filters: {
      name: UserModelPublicBaseFilterEnum
      value: any
    }[]
    limit: number
    offset: number
  } = {
    filters: [],
    limit: pageSize,
    offset: (page - 1) * pageSize
  }

  if (search) {
    variables.filters.push({
      name: UserModelPublicBaseFilterEnum.search,
      value: search
    })
  }
  const { data, refetch, loading } = useGroupByIdQuery({
    variables: {
      id: Number(params.groupId),
      ...variables
    }
  })

  const group = data?.admin?.group

  const handleToggleSettings = (visible: boolean): void => {
    if (!group) {
      return
    }

    setStateWithSpread({
      showSettings: visible
    })
  }

  const handleSearchChange = (search: string): void => {
    setStateWithSpread({
      search,
      pending: true
    })
  }

  const handlePageChange = (page: number): void => {
    setStateWithSpread({
      page,
      pending: true
    })
  }

  const handleRemoveUserFromGroup = async (): Promise<void> => {
    refetch()

    setStateWithSpread({
      removeFromGroupModalSetup: {
        show: false,
        userId: null,
        groupId: null
      }
    })
  }

  const handleRoleChange = async (options: {
    userId: number
    role: UserGroupRoleEnum
  }) => {
    const { userId, role } = options

    if (!group) {
      return
    }

    await userChangeGroupRole({
      variables: {
        groupId: group.id,
        userId,
        role
      }
    })

    refetch()
  }

  const handleToggleDeleteModal = (visible: boolean): void => {
    setStateWithSpread({ showDeleteModal: visible })
  }

  const handleToggleAddToGroupModal = (visible: boolean): void => {
    setStateWithSpread({ showSelectUserModal: visible })
  }

  const handleUserAddedToGroup = (): void => {
    setStateWithSpread({ showSelectUserModal: false })
    refetch()
  }
  if (!loading && !group) {
    return (
      <PageWithSingleAlertAndBackToLogin
        description={t`Group ${params.groupId} doesn't exist in your organisation`}
        title={t`No such group`}
      />
    )
  }

  return (
    <Page className="GroupPage">
      <PageHeader
        className="PageHeader"
        title={
          group && (
            <>
              <GroupAvatar group={group} /> {group.name}
            </>
          )
        }
        titleFollowers={
          <IconButton
            color="medium"
            hint={t`Settings`}
            icon={<IconSettings />}
            onClick={() => handleToggleSettings(true)}
          />
        }
        controls={
          <ButtonPrimary
            onClick={() => {
              if (group?.scimResource) {
                notification.warning(t`SCIM group cannot be modified in Looop.`)
                return
              }
              return handleToggleAddToGroupModal(true)
            }}
            leftIcon={<IconPlus />}
          >
            <Trans>Add to group</Trans>
          </ButtonPrimary>
        }
      />
      <ItemList
        className="GroupUsers__list"
        page={page}
        items={group?.users ?? []}
        itemsCount={group?.usersCount}
        pageSize={pageSize}
        header={
          <Flex mb="16px" w="100%" justifyContent="flex-end">
            <Filters search={search} onSearchChange={handleSearchChange} />
          </Flex>
        }
        renderItem={(user: IUser) => (
          <GroupListItem
            user={user}
            groupId={group?.id!}
            userClick={(event) => {
              // to prevent navigate when clicking on settings icon and select
              if (event.target.closest('.roleSelect, .Dropdown') === null) {
                navigateTo(linkBuilder.userDetail(user.id))
              }
            }}
            onRemoveClick={(userId, groupId) =>
              setStateWithSpread({
                removeFromGroupModalSetup: {
                  show: true,
                  userId,
                  groupId
                }
              })
            }
            onRoleChange={(role: UserGroupRoleEnum) =>
              handleRoleChange({
                userId: user.id,
                role
              })
            }
            onRemoveFromOtherGroup={handleRemoveUserFromGroup}
          />
        )}
        onPageChange={handlePageChange}
        mt={0}
      />
      {group && (
        <>
          <GroupSettingsDrawer
            visible={showSettings}
            group={group}
            refetchGroup={refetch}
            onHide={() => handleToggleSettings(false)}
            onDelete={() => handleToggleDeleteModal(true)}
          />
          {showDeleteModal && (
            <GroupDeleteModal
              show
              groupId={group?.id}
              onCanceled={() => handleToggleDeleteModal(false)}
              onDeleted={() => {
                navigateTo(staticLinks.groups)
              }}
            />
          )}
          <UserAddToGroupModal
            show={showSelectUserModal}
            groupId={group?.id}
            onHide={() => handleToggleAddToGroupModal(false)}
            onAdded={handleUserAddedToGroup}
          />
          <UserRemoveFromGroupModal
            show={state.removeFromGroupModalSetup.show}
            groupId={state.removeFromGroupModalSetup.groupId as number}
            userId={state.removeFromGroupModalSetup.userId as number}
            onCanceled={() => {
              setStateWithSpread({
                removeFromGroupModalSetup: {
                  show: false,
                  groupId: null,
                  userId: null
                }
              })
            }}
            onDeleted={handleRemoveUserFromGroup}
          />
        </>
      )}
    </Page>
  )
}
